import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import { Addresses } from "types";
import DataTable from "components/DataTable";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { getAuthCookie } from "../../utils/authCookies";

interface ExportRow {
  "Label of Address": string;
  "First Name": string;
  "Last Name": string;
  "Phone number": string;
  "Address line 1": string;
  "Address line 2": string;
  "City": string;
  "State": string;
  "Zipcode": string;
  "Country": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<Addresses>();

const RealtorAddresses = () => {
  const [addressObjects, setAddressObjects] = useState<Addresses[]>([]);
  const userId = getAuthCookie("userId");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null); // Track deletion in progress
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const token = getAuthCookie("sessionToken");
  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const tableRef = useRef<HTMLTableElement>(null);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);

  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/addresses/user/${userId}/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
            }),
          }
        );
        const data = await response.json();
        if (Array.isArray(data)) {
          setAddressObjects(data);
          setRowCount(data.length);
        } else if (data && Array.isArray(data.data)) {
          setAddressObjects(data.data);
          setRowCount(data.total || data.data.length);
        } else {
          setAddressObjects([]);
          setRowCount(0);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAddresses();
  }, [token, pagination, globalFilter,]);

  // Generate Excel export rows
  useEffect(() => {
    const rows: ExportRow[] = addressObjects.map((address) => ({
      "Label of Address": address.label || '',
      "First Name": address.first_name || '',
      "Last Name": address.last_name || '',
      "Phone number": address.address_details?.phone || '',
      "Address line 1": address.address_details?.addressLine1 || '',
      "Address line 2": address.address_details?.addressLine2 || '',
      "City": address.address_details?.city || '',
      "State": address.address_details?.state || '',
      "Zipcode": address.address_details?.zip || '',
      "Country": address.address_details?.country || '',
    }));

    setExportRows(rows);
  }, [addressObjects]);

  const handleDelete = async (addressId: string) => {

    setDeleting(addressId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/addresses/${addressId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        showError("Failed to delete the address.");
        throw new Error("Failed to delete the address.");
      }

      showSuccess("Brand deleted successfully!");
      setAddressObjects((prev) =>
        prev?.filter((address) => address._id !== addressId)
      );
      setDeleting(null);
      navigate(0);
    } catch (err) {
      showError("Failed to delete the address.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null);
    }
  };

  const columns = useMemo(() => [
    columnHelper.display({
      id: "serial",
      header: () => <span className="th-text S-No">S.No.</span>,
      cell: (info) => <span className="text-sm S-No">{pagination.pageIndex * pagination.pageSize + info.row.index + 1}</span>,
    }),
    columnHelper.display({
      id: "label",
      header: () => <span className="th-text S-No-2">Label of Address</span>,
      cell: (info) => {
        const address = info.row.original;
        return (
          <span className="text-sm S-No-2">
            <span className="status-badge status-default-btn ">{address.label}</span>
          </span>
        );
      },
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor("address_details.addressLine1", {
      header: () => <span className="th-text">Address line 1</span>,
      cell: info => <span className="text-sm">{info.getValue() ?? ""}</span>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor("address_details.city", {
      header: () => <span className="th-text">City</span>,
      cell: info => <span className="text-sm">{info.getValue() ?? ""}</span>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor("address_details.state", {
      header: () => <span className="th-text">State</span>,
      cell: info => <span className="text-sm">{info.getValue() ?? ""}</span>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor("address_details.country", {
      header: () => <span className="th-text">Country</span>,
      cell: info => <span className="text-sm">{info.getValue() ?? ""}</span>,
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
    }),
    columnHelper.display({
      id: "actions",
      header: () => <span className="th-text">Actions</span>,
      cell: (info) => {
        const address = info.row.original;
        return (
          <span className="d-flex  gap-2">
            {/* <Link
              to={`/realtor/editAddress/${address._id}`}
              className="edit me-2"
              title="Edit"
            >
              <i className="ri-pencil-fill fs-20"></i>
            </Link> */}
            <EditIconButton
              to={`${baseRoute}/editAddresses/${address._id}`}
              state={{ type: "edit" }}
              className="icon-square"
              title="Edit"
            />

            <DeleteIconButton
              className="icon-square danger"
              title="Delete"
              onClick={() =>
                window.confirm("Are you sure you want to delete this team member?") &&
                handleDelete(address._id)
              }
              disabled={deleting === address._id}
            />

          </span>
        );
      },
    }),
  ], [deleting, pagination.pageIndex, pagination.pageSize]);


  // const columns = useMemo(() => {
  //   return [
  //     columnHelper.display({
  //       id: "serial",
  //       header: "S.No.",
  //       cell: (info) => pagination.pageIndex * pagination.pageSize + info.row.index + 1,
  //     }),
  //     columnHelper.display({
  //       header: "Label of Address",
  //       cell: (info) => {
  //         const address = info.row.original;
  //         return (
  //           <span className="status-badge status-default-btn w-100">{address.label}</span>
  //         );
  //       },
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //     }),
  //     columnHelper.accessor("address_details.addressLine1", {
  //       header: "Address line 1",
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //     }),
  //     columnHelper.accessor("address_details.city", {
  //       header: "City",
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //     }),
  //     columnHelper.accessor("address_details.state", {
  //       header: "State",
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //     }),
  //     columnHelper.accessor("address_details.country", {
  //       header: "Country",
  //       filterFn: fuzzyTextFilter,
  //       enableGlobalFilter: true,
  //     }),
  //     columnHelper.display({
  //       id: "actions",
  //       header: "Actions",
  //       cell: (info) => {
  //         const address = info.row.original;
  //         return (
  //           <>
  //             <Link
  //               to={`/realtor/editAddress/${address._id}`}
  //               className="edit me-3"
  //             >
  //               <i className="ri-pencil-fill fs-20"></i>
  //             </Link>
  //             <button
  //               className="del border-0 p-0"
  //               onClick={() => handleDelete(address._id)}
  //               disabled={deleting === address._id}
  //             >
  //               <i className="ri-delete-bin-fill fs-18"></i>
  //             </button>
  //           </>
  //         );
  //       },
  //     }),
  //   ];
  // }, [deleting]);

  const table = useReactTable({
    data: addressObjects,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <div className="content container">

      {/* <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0 "></h4>
        <div className="d-flex gap-3 align-items-center">
          <Link to={`${baseRoute}/addAgentAddresses`} className="btn btn-primary  table-addbutton">
            + Add Address
          </Link>
        </div>
      </div> */}
      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <h4 className="po-title mb-0"> Addresses</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Label Name, City, Contact Person" value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setGlobalFilter(searchInput);
                  }
                }}
              />
            </div>
             <Link to={`${baseRoute}/addAgentAddresses`} className="btn btn-primary  table-addbutton">
            + Add Address
          </Link>
            <ExportAsExcel
              data={exportRows}
              headers={[
                "Label of Address",
                "First Name",
                "Last Name",
                "Phone number",
                "Address line 1",
                "Address line 2",
                "City",
                "State",
                "Zipcode",
                "Country",
              ]}
              fileName="Addresses"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                  orders
                </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
        </div>

      </div>
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="mt-4 mb-4 ">
          <TablePaginationControls table={table} />
        </div>
      )}
    </div>
  );
};

export default RealtorAddresses;
